import React, { useState } from "react";
import { Box, Paper, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, List, Alert, Tabs, Tab } from "@mui/material";
import { ExpandMore as ExpandMoreIcon, BugReport as BugReportIcon } from "@mui/icons-material";
import { MessageItem } from "./AgentChat/MessageItem";
import { ChatMessage } from "../types";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`message-tabpanel-${index}`} aria-labelledby={`message-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const MessageItemViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  // All sections expanded by default
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    regular: true,
    events: true,
    actionProposals: true,
    actionExecutions: true,
    sources: true,
    errors: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Example messages for different types
  const exampleMessages: { [key: string]: ChatMessage[] } = {
    // Regular Messages
    regular: [
      {
        id: "nzZsmYNO63iqpCrwVFtU",
        type: "agent",
        content:
          "Please provide me with the following details for the new haircut calendar event:\n1. **Title** (e.g., 'Haircut')\n2. **Date and Time** (you mentioned 10 AM tomorrow, which would be Wednesday, October 15, 2025, at 10 AM BST)\n3. **Duration** (how long you expect the haircut to take, e.g., 1 hour)\n4. **Location** (if you want to include it)\n\nOnce I have this information, I'll create the calendar event for you.",
        timestamp: new Date("2025-10-14T15:34:58.907Z"),
        timestampMs: 1760456098907,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: true,
      },
      {
        id: "MwsMny12w0CUISy9UatC",
        type: "user",
        content: "I've booked a haircut for 10am tomorrow, please can you add it",
        metadata: {
          processed: true,
        },
        timestamp: new Date("2025-10-14T15:34:41.493Z"),
        timestampMs: 1760456081493,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: true,
      },
    ],

    // Events
    events: [
      {
        id: "event-1",
        type: "event",
        content: "New calendar event created: Team Meeting",
        timestamp: new Date(),
        threadId: "thread-1",
        agentId: "",
        read: true,
        metadata: {
          eventType: "calendar_event_created",
          data: {
            title: "Team Meeting",
            startTime: "2025-01-15T10:00:00",
            endTime: "2025-01-15T11:00:00",
            location: "Conference Room A",
          },
        },
      },
      {
        id: "event-2",
        type: "event",
        content: "Calendar event updated: Project Review",
        timestamp: new Date(),
        threadId: "thread-1",
        agentId: "",
        read: true,
        metadata: {
          eventType: "calendar_event_updated",
          data: {
            title: "Project Review",
            startTime: "2025-01-16T14:00:00",
            endTime: "2025-01-16T15:30:00",
            location: "Main Conference Room",
          },
        },
      },
    ],

    // Action Proposals
    actionProposals: [
      {
        id: "action_KhZbBcp1VvBWpzDjaHf8",
        type: "action",
        content: "Proposed action: Create Haircut Appointment",
        metadata: {
          actionId: "KhZbBcp1VvBWpzDjaHf8",
          actionType: "Calendar.Entry.Create",
          status: "proposed",
          reason: "To schedule a haircut appointment based on the provided details.",
          requiresApproval: true,
          title: "Create Haircut Appointment",
          parameters: {
            title: "Haircut",
            description: "Haircut appointment at Liverpool Street",
            location: "Liverpool Street",
            startTime: "2025-10-15T10:00:00",
            endTime: "2025-10-15T11:00:00",
            timezone: "Europe/London",
          },
        },
        timestamp: new Date("2025-10-14T15:35:25.284Z"),
        timestampMs: 1760456125284,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: true,
      },
      {
        id: "action_mod9KuJ6czu757EiFQkC",
        type: "action",
        content: "Proposed action: Draft Email to Ben",
        metadata: {
          actionId: "mod9KuJ6czu757EiFQkC",
          actionType: "Email.Draft.Create",
          status: "proposed",
          reason: "To create an email draft asking Ben for his availability for tomorrow.",
          requiresApproval: true,
          title: "Draft Email to Ben",
          parameters: {
            to: "ben@example.com",
            subject: "Availability for Tomorrow",
            body: "Hi Ben,\n\nI hope this message finds you well. I wanted to check if you have any availability for a meeting tomorrow. Please let me know your available time slots.\n\nThank you!\n\nBest regards,\nJosh",
          },
        },
        timestamp: new Date("2025-10-14T17:33:00.929Z"),
        timestampMs: 1760463180929,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: false,
      },
      {
        id: "action_JrUQ48N40gO34JHPEYnH",
        type: "action",
        content: "Proposed action: Add Priority: Personal Health",
        metadata: {
          actionId: "JrUQ48N40gO34JHPEYnH",
          actionType: "Priority.Add",
          status: "proposed",
          reason: "To emphasize the importance of personal health as a priority.",
          requiresApproval: true,
          title: "Add Priority: Personal Health",
          parameters: {
            title: "Personal Health",
            priorityName: "Health & Wellness",
            description: "Focus on maintaining and improving personal health and well-being.",
            category: "Personal",
          },
        },
        timestamp: new Date("2025-10-14T17:35:52.565Z"),
        timestampMs: 1760463352565,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: false,
      },
      {
        id: "action_Bw3EMSPjOLRGIsA0EdEP",
        type: "action",
        content: "Proposed action: Move Haircut Appointment to 11 AM Tomorrow",
        metadata: {
          actionId: "Bw3EMSPjOLRGIsA0EdEP",
          actionType: "Calendar.Entry.Move",
          status: "proposed",
          reason: "To reschedule the haircut appointment to the requested time.",
          requiresApproval: true,
          title: "Move Haircut Appointment to 11 AM Tomorrow",
          parameters: {
            eventId: "rhrt0k7fa4ks0ft1s4618aklt8",
            newStartTime: "2025-10-15T11:00:00",
            newEndTime: "2025-10-15T12:00:00",
            timezone: "Europe/London",
            notifyAttendees: true,
          },
        },
        timestamp: new Date("2025-10-14T17:35:32.212Z"),
        timestampMs: 1760463332212,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: false,
      },
      {
        id: "action_lE2RT2pJE4yVofMI3Cdh",
        type: "action",
        content: "Proposed action: Create Todo: Follow Up with Ben",
        metadata: {
          actionId: "lE2RT2pJE4yVofMI3Cdh",
          actionType: "Todo.Add",
          status: "proposed",
          reason: "To ensure a follow-up on the email sent to Ben regarding his availability.",
          requiresApproval: true,
          title: "Create Todo: Follow Up with Ben",
          parameters: {
            title: "Follow up on availability email to Ben",
            todoName: "Follow Up with Ben",
            priority: "high",
          },
        },
        timestamp: new Date("2025-10-14T17:35:07.937Z"),
        timestampMs: 1760463307937,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: false,
      },
      {
        id: "action_JVq8lhFd7yY4uwToUxpa",
        type: "action",
        content: "Proposed action: Reorder Bug-free Experience",
        metadata: {
          actionId: "JVq8lhFd7yY4uwToUxpa",
          actionType: "Priority.ChangeOrder",
          status: "proposed",
          reason: "To rearrange the order after moving Personal Health to the top.",
          requiresApproval: true,
          title: "Reorder Bug-free Experience",
          parameters: {
            priorityId: "BTnKIpFMez7MzW1A2yQq",
            newOrder: 4,
          },
        },
        timestamp: new Date("2025-10-15T14:02:42.803Z"),
        timestampMs: 1760536962803,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: false,
      },
      {
        id: "action_wkZoOXFedLwHKl0UJWNf",
        type: "action",
        content: "Proposed action: Create Socializing Event",
        metadata: {
          actionId: "wkZoOXFedLwHKl0UJWNf",
          actionType: "Calendar.Entry.Create",
          status: "proposed",
          reason: "To schedule a socializing event and invite you.",
          requiresApproval: true,
          title: "Create Socializing Event",
          parameters: {
            title: "Socializing",
            description: "Socializing event in Hackney",
            location: "Hackney",
            startTime: "2025-10-15T19:00:00",
            endTime: "2025-10-15T20:00:00",
            timezone: "Europe/London",
            attendees: [
              {
                email: "josh@example.com",
                displayName: "Josh",
              },
            ],
          },
        },
        timestamp: new Date("2025-10-15T14:41:19.042Z"),
        timestampMs: 1760539279042,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: false,
      },
    ],

    // Action Executions
    actionExecutions: [
      {
        id: "action-execution-1",
        type: "action_execution",
        content: "Calendar event created successfully.",
        timestamp: new Date(),
        threadId: "thread-1",
        agentId: "assistant-1",
        read: true,
        actionType: "Calendar.Entry.Create",
        data: {
          actionId: "KhZbBcp1VvBWpzDjaHf8",
          actionType: "Calendar.Entry.Create",
          status: "completed",
          result: {
            id: "event-123",
            title: "Haircut",
            description: "Haircut appointment at Liverpool Street",
            location: "Liverpool Street",
            startTime: "2025-10-15T10:00:00",
            endTime: "2025-10-15T11:00:00",
            timezone: "Europe/London",
            calendarId: "primary",
            eventId: "event-123",
          },
        },
        metadata: {
          actionId: "KhZbBcp1VvBWpzDjaHf8",
          actionType: "Calendar.Entry.Create",
          actionStatus: "executed",
          status: "completed",
          result: {
            id: "event-123",
            title: "Haircut",
            description: "Haircut appointment at Liverpool Street",
            location: "Liverpool Street",
            startTime: "2025-10-15T10:00:00",
            endTime: "2025-10-15T11:00:00",
            timezone: "Europe/London",
            calendarId: "primary",
            eventId: "event-123",
          },
        },
      },

      {
        id: "action_wkZoOXFedLwHKl0UJWNf",
        type: "action",
        content: "Action completed: Calendar.Entry.Create",
        metadata: {
          actionId: "wkZoOXFedLwHKl0UJWNf",
          actionType: "Calendar.Entry.Create",
          status: "completed",
          parameters: {
            title: "Socializing (event with attendee)",
            description: "Socializing event in Hackney",
            location: "Hackney",
            startTime: "2025-10-15T19:00:00",
            endTime: "2025-10-15T20:00:00",
            timezone: "Europe/London",
            attendees: [
              {
                email: "josh@example.com",
                displayName: "Josh",
              },
            ],
          },
          result: {
            eventId: "event_1760539450383_yrlc2cn5h",
            kind: "calendar#event",
            etag: '"3521078900473150"',
            id: "hsru7ffkp6eqo9ois48evptac8",
            status: "confirmed",
            htmlLink: "https://www.google.com/calendar/event?eid=aHNydTdmZmtwNmVxbzlvaXM0OGV2cHRhYzggZGV2QHNjaGVkYW5hLmNvbQ",
            created: "2025-10-15T14:44:10.000Z",
            updated: "2025-10-15T14:44:10.236Z",
            summary: "Socializing Event",
            description: "Socializing event in Hackney",
            location: "Hackney",
            creator: {
              email: "dev@schedana.com",
              self: true,
            },
            organizer: {
              email: "dev@schedana.com",
              self: true,
            },
            start: {
              dateTime: "2025-10-15T20:00:00+01:00",
              timeZone: "Europe/London",
            },
            end: {
              dateTime: "2025-10-15T21:00:00+01:00",
              timeZone: "Europe/London",
            },
            iCalUID: "hsru7ffkp6eqo9ois48evptac8@google.com",
            sequence: 0,
            attendees: [
              {
                email: "josh@example.com",
                displayName: "Josh",
                responseStatus: "needsAction",
              },
            ],
            reminders: {
              useDefault: true,
            },
            eventType: "default",
          },
        },
        timestamp: new Date("2025-10-15T14:41:19.042Z"),
        timestampMs: 1760539279042,
        threadId: "0l1YW5nOiV8MnlIFBFl7",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: true,
      },
      {
        id: "action-execution-2",
        type: "action_execution",
        content: "Coach plan saved successfully.",
        timestamp: new Date(),
        threadId: "thread-1",
        agentId: "assistant-1",
        read: true,
        actionType: "Coach.Plan.Save",
        data: {
          actionId: "coach-plan-1",
          actionType: "Coach.Plan.Save",
          status: "completed",
          result: {
            id: "plan-456",
            title: "Productivity Improvement Plan",
            overview: "A comprehensive plan to improve daily productivity",
            goals: {
              goal1: "Complete all high-priority tasks by 2 PM",
              goal2: "Reduce meeting time by 30%",
              goal3: "Implement time-blocking technique",
            },
            actionItems: ["Set up time-blocking calendar", "Review daily priorities each morning", "Use Pomodoro technique for focused work"],
            trackingMetrics: ["Tasks completed on time", "Meeting duration tracking", "Focus time per day"],
          },
        },
        metadata: {
          actionId: "coach-plan-1",
          actionType: "Coach.Plan.Save",
          actionStatus: "executed",
          status: "completed",
        },
      },
    ],

    // Source Messages
    sources: [
      {
        id: "source-request-1",
        type: "source_request",
        content: "Fetching your calendar events for the next week...",
        timestamp: new Date(),
        threadId: "thread-1",
        agentId: "assistant-1",
        read: true,
        metadata: {
          sourceId: "calendar",
          reason: "To check your availability for scheduling",
          data: {
            startDate: "2025-01-15",
            endDate: "2025-01-22",
            calendarId: "primary",
          },
        },
      },
      {
        id: "source-response-1",
        type: "source_response",
        content: "Found 5 calendar events for the next week.",
        timestamp: new Date(),
        threadId: "thread-1",
        agentId: "assistant-1",
        read: true,
        metadata: {
          sourceId: "calendar",
          data: [
            {
              id: "event-1",
              title: "Team Meeting",
              startTime: "2025-01-15T10:00:00",
              endTime: "2025-01-15T11:00:00",
              location: "Conference Room A",
            },
            {
              id: "event-2",
              title: "Project Review",
              startTime: "2025-01-16T14:00:00",
              endTime: "2025-01-16T15:30:00",
              location: "Main Conference Room",
            },
          ],
        },
      },
      {
        id: "source-error-1",
        type: "source_request",
        content: "Failed to fetch calendar events.",
        timestamp: new Date(),
        threadId: "thread-1",
        agentId: "assistant-1",
        read: true,
        metadata: {
          sourceId: "calendar",
          error: "CALENDAR_ACCESS_DENIED: Unable to access calendar. Please check permissions.",
        },
      },
    ],

    // Error Messages
    errors: [
      {
        id: "action_3sm5uFtpMN7dfWz7ltDy",
        type: "action",
        content: "Action failed: Calendar.Entry.Move",
        timestamp: new Date("2025-10-10T22:39:28.481Z"),
        threadId: "thread-1",
        agentId: "bSWRRuQabfVHk6edRLRJHAWsjoj1_PersonalAssistantAgent",
        read: true,
        metadata: {
          actionId: "3sm5uFtpMN7dfWz7ltDy",
          actionType: "Calendar.Entry.Move",
          status: "proposed",
          reason: "To reschedule the haircut appointment to the correct time in BST.",
          requiresApproval: true,
          title: "Move Haircut Appointment to Sunday at 12 PM BST",
          parameters: {
            eventId: "rhrt0k7fa4ks0ft1s4618aklt8",
            newStartTime: "2025-10-12T11:00:00",
            newEndTime: "2025-10-12T12:00:00",
            notifyAttendees: true,
          },
          error: "User not connected to Google Calendar",
        },
      },
      {
        id: "source-error-2",
        type: "source_request",
        content: "Failed to fetch calendar events due to authentication error.",
        timestamp: new Date(),
        threadId: "thread-1",
        agentId: "assistant-1",
        read: true,
        metadata: {
          sourceId: "calendar",
          error: "AUTHENTICATION_FAILED: Google Calendar authentication expired",
        },
      },
      {
        id: "action-error-2",
        type: "action_proposal",
        content: "Unable to create calendar event due to insufficient permissions.",
        timestamp: new Date(),
        threadId: "thread-1",
        agentId: "assistant-1",
        read: true,
        actionType: "Calendar.Entry.Create",
        metadata: {
          actionId: "error-action-2",
          actionType: "Calendar.Entry.Create",
          status: "failed",
          reason: "To create a new meeting appointment",
          error: "INSUFFICIENT_PERMISSIONS: Cannot create events in this calendar",
        },
      },
    ],
  };

  const handleExecuteAction = (actionId: string) => {
    console.log("Execute action:", actionId);
    // In a real app, this would trigger the action execution
  };

  const handleIgnoreAction = (actionId: string) => {
    console.log("Ignore action:", actionId);
    // In a real app, this would mark the action as ignored
  };

  const renderMessageSection = (title: string, messages: ChatMessage[], sectionKey: string) => (
    <Accordion expanded={expandedSections[sectionKey]} onChange={() => toggleSection(sectionKey)} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">{title}</Typography>
          <Chip label={messages.length} size="small" color="primary" />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
          <List>
            {messages.map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                onExecuteAction={handleExecuteAction}
                onIgnoreAction={handleIgnoreAction}
                agentNames={{ "assistant-1": "AI Assistant" }}
                agentAvatars={{ "assistant-1": undefined }}
                userTimezone="Europe/London"
              />
            ))}
          </List>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );

  const tabs = [
    { label: "All Messages", value: 0 },
    { label: "Regular Messages", value: 1 },
    { label: "Events", value: 2 },
    { label: "Action Proposals", value: 3 },
    { label: "Action Executions", value: 4 },
    { label: "Source Messages", value: 5 },
    { label: "Error Messages", value: 6 },
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <BugReportIcon sx={{ fontSize: 32, color: "primary.main" }} />
        <Box>
          <Typography variant="h4" gutterBottom>
            Message Item Viewer
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Debug page showing all message types, actions, sources, and events as they appear in chat
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        This page demonstrates how different message types are rendered in the chat interface. Use the tabs below to explore different categories of messages.
      </Alert>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto">
          {tabs.map((tab) => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <Typography variant="h5" gutterBottom>
          All Message Types
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Complete overview of all message types in the chat system
        </Typography>

        {renderMessageSection("Regular Messages", exampleMessages.regular, "regular")}
        {renderMessageSection("Events", exampleMessages.events, "events")}
        {renderMessageSection("Action Proposals", exampleMessages.actionProposals, "actionProposals")}
        {renderMessageSection("Action Executions", exampleMessages.actionExecutions, "actionExecutions")}
        {renderMessageSection("Source Messages", exampleMessages.sources, "sources")}
        {renderMessageSection("Error Messages", exampleMessages.errors, "errors")}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Typography variant="h5" gutterBottom>
          Regular Messages
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          User, agent, and system messages
        </Typography>
        {renderMessageSection("Regular Messages", exampleMessages.regular, "regular-single")}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Typography variant="h5" gutterBottom>
          Events
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Calendar and system events
        </Typography>
        {renderMessageSection("Events", exampleMessages.events, "events-single")}
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <Typography variant="h5" gutterBottom>
          Action Proposals
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Actions proposed by the AI that require user approval
        </Typography>
        {renderMessageSection("Action Proposals", exampleMessages.actionProposals, "actionProposals-single")}
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <Typography variant="h5" gutterBottom>
          Action Executions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Actions that have been executed successfully
        </Typography>
        {renderMessageSection("Action Executions", exampleMessages.actionExecutions, "actionExecutions-single")}
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <Typography variant="h5" gutterBottom>
          Source Messages
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Data source requests and responses
        </Typography>
        {renderMessageSection("Source Messages", exampleMessages.sources, "sources-single")}
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <Typography variant="h5" gutterBottom>
          Error Messages
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Failed actions and error states
        </Typography>
        {renderMessageSection("Error Messages", exampleMessages.errors, "errors-single")}
      </TabPanel>
    </Box>
  );
};
